import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ArrowDownRight, ShieldCheck } from "lucide-react";
import { fmtINR, fmtPct, fmtQty, colorByValue } from "@/lib/format";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from "recharts";

const PALETTE = ["#2B8D7D", "#6FD3C4", "#A9E7DD", "#7E909F", "#4A5560", "#2B4A63", "#8A9099", "#0F4A42"];
const MCAP_LABEL = { LARGE: "Large", MID: "Mid", SMALL: "Small" };
const MCAP_COLOR = { LARGE: "#2B8D7D", MID: "#6FD3C4", SMALL: "#A9E7DD" };

export default function PublicShare() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    axios
      .get(`${base}/api/public/share/${shareId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This share link is no longer available."))
      .finally(() => setLoading(false));
  }, [shareId]);

  const allocation = useMemo(() => {
    if (!data?.holdings) return [];
    return data.holdings
      .filter((h) => (h.current_value || 0) > 0)
      .map((h, i) => ({
        name: `${h.symbol}·${h.exchange}`,
        value: Number(h.current_value || 0),
        color: PALETTE[i % PALETTE.length],
      }));
  }, [data]);

  const marketCapTiles = useMemo(() => {
    const m = data?.summary?.market_cap_allocation || {};
    return ["LARGE", "MID", "SMALL"]
      .map((k) => ({ key: k, label: `${MCAP_LABEL[k]} Cap`, color: MCAP_COLOR[k], value: m[k]?.value || 0, pct: m[k]?.pct || 0 }))
      .filter((b) => b.value > 0);
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center label-overline" data-testid="public-share-loading">
        Loading shared portfolio…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--bg-base)" }}>
        <div className="surface-card p-10 max-w-md text-center" data-testid="public-share-error">
          <p className="label-overline mb-2">Not available</p>
          <p className="font-display text-2xl mb-3">{error}</p>
          <Link to="/" className="text-sm" style={{ color: "var(--accent-primary)" }}>
            Visit EquityBox →
          </Link>
        </div>
      </div>
    );
  }

  const s = data.summary || {};
  const isPositive = (s.absolute_gain || 0) >= 0;

  return (
    <div className="theme-light min-h-screen" style={{ background: "var(--bg-base)", color: "var(--text-primary)" }} data-testid="public-share-page">
      {/* Header — minimal brand bar, no nav */}
      <header
        className="sticky top-0 z-30"
        style={{ background: "var(--bg-base)", borderBottom: "1px solid var(--border-soft)" }}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 md:py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center" data-testid="public-brand">
            <img
              src="/brand/logo-light.png"
              alt="EquityBox — Vedansh Wealth LLP"
              className="h-10 w-auto"
              style={{ maxHeight: 40 }}
              data-testid="public-brand-logo"
            />
          </Link>
          <span
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono px-2.5 py-1 rounded"
            style={{ background: "var(--tint-accent-bg)", color: "var(--tint-accent-fg)" }}
          >
            <ShieldCheck className="w-3 h-3" strokeWidth={2} />
            Read-only
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Hero */}
        <section className="mb-6 md:mb-10">
          <p className="label-overline mb-2">
            {data.display_name}&apos;s portfolio · INR
            {s.inception_date ? ` · since ${formatDate(s.inception_date)}` : ""}
          </p>
          <h1 className="font-display text-[34px] md:text-[44px] font-bold tracking-[-0.035em] leading-[1.05]">
            {fmtINR(s.current_value)}{" "}
            <span className={isPositive ? "text-positive" : "text-negative"}>
              ({fmtPct(s.absolute_return_pct)})
            </span>
          </h1>
          <p className="text-sm md:text-base mt-2 max-w-xl" style={{ color: "var(--text-secondary)" }}>
            {isPositive ? "Up" : "Down"} {fmtINR(Math.abs(s.absolute_gain || 0))} from{" "}
            {fmtINR(s.initial_capital)} day-1 capital
            {s.duration_days ? ` · ${formatDuration(s.duration_days)}` : ""}.
          </p>
        </section>

        {/* Hero metric grid */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 mb-6 md:mb-8" data-testid="public-summary-cards">
          <PublicMetric label="Initial capital" value={fmtINR(s.initial_capital)} hint="Day-1 deployed" />
          <PublicMetric label="Current value" value={fmtINR(s.current_value)} hint="Live market" />
          <PublicMetric
            label="Absolute gain"
            value={fmtINR(s.absolute_gain, { showSign: true })}
            valueClass={colorByValue(s.absolute_gain)}
            trend={(s.absolute_gain || 0) >= 0 ? "up" : "down"}
            hint="Unrealized + Realized + Dividends"
          />
          <PublicMetric
            label="XIRR"
            value={s.xirr_pct == null ? "—" : fmtPct(s.xirr_pct)}
            valueClass={colorByValue(s.xirr_pct || 0)}
            hint="Money-weighted, annualised"
          />
        </section>

        {/* Sub-metrics */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 mb-6 md:mb-8">
          <SubStat label="Day's gain" value={fmtINR(s.day_gain, { showSign: true })} tone={s.day_gain} sub={fmtPct(s.day_gain_pct)} />
          <SubStat label="Unrealized" value={fmtINR(s.unrealized_gain, { showSign: true })} tone={s.unrealized_gain} />
          <SubStat label="Realized" value={fmtINR(s.realized_gain, { showSign: true })} tone={s.realized_gain} />
          <SubStat label="Dividends" value={fmtINR(s.dividends)} />
        </section>

        {/* Allocation + Market-cap */}
        <section className="grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-6 mb-6 md:mb-8">
          {data.show_holdings !== false && (
          <div className="surface-card p-5 md:p-7 lg:col-span-3" data-testid="public-allocation">
            <p className="label-overline mb-1">Allocation</p>
            <h3 className="font-display text-lg md:text-xl font-medium tracking-tight mb-4 md:mb-5">Where the capital lives</h3>
            {allocation.length === 0 ? (
              <div className="h-48 flex items-center justify-center label-overline">No active holdings</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 items-center">
                <div className="h-56 md:h-64">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={allocation} dataKey="value" nameKey="name" innerRadius={50} outerRadius={84} paddingAngle={2}>
                        {allocation.map((e) => <Cell key={e.name} fill={e.color} />)}
                      </Pie>
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid var(--border-soft)" }}
                        formatter={(v) => fmtINR(v)}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="space-y-2 text-sm">
                  {allocation.slice(0, 8).map((a) => {
                    const total = allocation.reduce((sum, x) => sum + x.value, 0) || 1;
                    const pct = (a.value / total) * 100;
                    return (
                      <li key={a.name} className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: a.color }} />
                          <span className="font-medium">{a.name}</span>
                        </span>
                        <span className="font-mono text-xs" style={{ color: "var(--text-secondary)" }}>{pct.toFixed(1)}%</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
          )}

          <div className="surface-card p-5 md:p-7 lg:col-span-2" data-testid="public-marketcap">
            <p className="label-overline mb-1">Market-cap mix</p>
            <h3 className="font-display text-lg md:text-xl font-medium tracking-tight mb-4">Large · Mid · Small</h3>
            {marketCapTiles.length === 0 ? (
              <div className="h-32 flex items-center justify-center label-overline">No holdings</div>
            ) : (
              <>
                <ul className="space-y-3 mb-4">
                  {marketCapTiles.map((b) => (
                    <li key={b.key} className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 text-sm">
                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: b.color }} />
                        <span className="font-medium">{b.label}</span>
                      </span>
                      <span className="text-right">
                        <div className="font-mono text-sm">{fmtINR(b.value)}</div>
                        <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>{b.pct.toFixed(1)}%</div>
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="h-1.5 w-full rounded-sm overflow-hidden flex">
                  {marketCapTiles.map((b) => (
                    <div key={b.key} title={`${b.label}: ${b.pct.toFixed(1)}%`} style={{ width: `${b.pct}%`, background: b.color }} />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* Holdings */}
        {data.show_holdings !== false && (
        <section className="surface-card overflow-hidden mb-8" data-testid="public-holdings">
          <div className="p-5 md:p-7 flex items-center justify-between">
            <div>
              <p className="label-overline mb-1">Holdings</p>
              <h3 className="font-display text-lg md:text-xl font-medium tracking-tight">{data.holdings.length} active position{data.holdings.length === 1 ? "" : "s"}</h3>
            </div>
          </div>
          {data.holdings.length === 0 ? (
            <div className="px-5 md:px-7 pb-8 label-overline" data-testid="public-holdings-empty">
              No active holdings — owner has sold out or has no positions yet.
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden flex flex-col divide-y" style={{ borderColor: "var(--border-soft)" }}>
                {data.holdings.map((h) => (
                  <PublicHoldingCard key={`${h.symbol}:${h.exchange}-card`} h={h} showQty={data.show_quantities} showAvg={data.show_avg_cost} />
                ))}
              </div>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bg-base)" }}>
                <tr>
                  <Th>Symbol</Th>
                  {data.show_quantities && <Th right>Qty</Th>}
                  {data.show_avg_cost && <Th right>Avg cost</Th>}
                  <Th right>Current</Th>
                  <Th right>Day&apos;s gain</Th>
                  <Th right>Value</Th>
                  <Th right>Unrealized</Th>
                </tr>
              </thead>
              <tbody>
                {data.holdings.map((h) => (
                  <tr key={`${h.symbol}:${h.exchange}`} className="border-t" style={{ borderColor: "var(--border-soft)" }} data-testid={`public-row-${h.symbol}`}>
                    <td className="py-4 px-6">
                      <div className="font-medium flex items-center gap-2">
                        {h.symbol}
                        <span
                          className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{
                            background: h.exchange === "BSE" ? "var(--tint-neutral-bg)" : "var(--tint-accent-bg)",
                            color: h.exchange === "BSE" ? "var(--tint-neutral-fg)" : "var(--tint-accent-fg)",
                          }}
                        >
                          {h.exchange || "NSE"}
                        </span>
                        {h.market_cap && (
                          <span
                            className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ background: "var(--tint-accent-bg)", color: "var(--tint-accent-fg)" }}
                          >
                            {MCAP_LABEL[h.market_cap]}
                          </span>
                        )}
                      </div>
                      {h.company_name && <div className="text-xs" style={{ color: "var(--text-muted)" }}>{h.company_name}</div>}
                    </td>
                    {data.show_quantities && <Td right mono>{fmtQty(h.quantity)}</Td>}
                    {data.show_avg_cost && <Td right mono>{fmtINR(h.avg_cost, { decimals: 2 })}</Td>}
                    <Td right mono>{h.current_price > 0 ? fmtINR(h.current_price) : "—"}</Td>
                    <Td right mono className={colorByValue(h.day_gain)}>
                      {h.previous_close ? (
                        <>
                          <div>{fmtINR(h.day_gain, { showSign: true })}</div>
                          <div className="text-[10px] font-normal" style={{ color: "var(--text-muted)" }}>{fmtPct(h.day_gain_pct)}</div>
                        </>
                      ) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                    </Td>
                    <Td right mono>{fmtINR(h.current_value)}</Td>
                    <Td right mono className={colorByValue(h.unrealized_gain)}>
                      {fmtINR(h.unrealized_gain, { showSign: true })}
                      <div className="text-[10px] font-normal" style={{ color: "var(--text-muted)" }}>{fmtPct(h.unrealized_pct)}</div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            </>
          )}
        </section>
        )}

        {/* Footer CTA */}
        <footer className="text-center pb-10">
          <p className="text-xs mb-2" style={{ color: "var(--text-secondary)" }}>
            Want this for your portfolio?
          </p>
          <Link
            to="/"
            data-testid="public-cta-equitybox"
            className="inline-flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-xl text-white transition-all hover:-translate-y-0.5"
            style={{ background: "var(--text-primary)" }}
          >
            Start tracking on EquityBox
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
          </Link>
        </footer>
      </main>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function formatDuration(days) {
  if (!days || days < 0) return "";
  if (days < 30) return `${days}d`;
  const years = days / 365;
  if (years >= 1) {
    const y = Math.floor(years);
    const remDays = Math.round((years - y) * 365);
    const months = Math.round(remDays / 30);
    return months > 0 ? `${y}y ${months}m` : `${y}y`;
  }
  return `${Math.round(days / 30)}m`;
}

function PublicMetric({ label, value, hint, valueClass = "", trend }) {
  return (
    <div className="surface-card p-4 md:p-6">
      <div className="flex items-start justify-between gap-2">
        <p className="label-overline text-[10px] md:text-[0.7rem]">{label}</p>
        {trend === "up" && <ArrowUpRight className="w-4 h-4 shrink-0" style={{ color: "var(--accent-positive)" }} strokeWidth={1.75} />}
        {trend === "down" && <ArrowDownRight className="w-4 h-4 shrink-0" style={{ color: "var(--accent-negative)" }} strokeWidth={1.75} />}
      </div>
      <p className={`font-metric text-lg md:text-2xl xl:text-[1.7rem] font-light mt-2 md:mt-3 tracking-tight ${valueClass}`}>{value}</p>
      {hint && <p className="text-[10px] md:text-[11px] mt-1.5 md:mt-2" style={{ color: "var(--text-muted)" }}>{hint}</p>}
    </div>
  );
}

function SubStat({ label, value, tone, sub }) {
  const toneClass = tone === undefined ? "" : colorByValue(tone);
  return (
    <div className="surface-card p-4 md:p-5">
      <p className="label-overline text-[10px] md:text-[0.7rem]">{label}</p>
      <p className={`font-metric text-base md:text-xl font-light mt-1.5 ${toneClass}`}>{value}</p>
      {sub && <p className="text-[10px] md:text-[11px] mt-0.5 font-mono" style={{ color: "var(--text-muted)" }}>{sub}</p>}
    </div>
  );
}

function Th({ children, right }) {
  return (
    <th className={`label-overline py-3 px-6 ${right ? "text-right" : "text-left"}`} style={{ color: "var(--text-muted)", fontSize: "0.65rem" }}>
      {children}
    </th>
  );
}

function Td({ children, right, mono, className = "" }) {
  return <td className={`py-4 px-6 ${right ? "text-right" : ""} ${mono ? "font-mono" : ""} ${className}`}>{children}</td>;
}

function PublicHoldingCard({ h, showQty, showAvg }) {
  return (
    <div className="px-4 py-4" data-testid={`public-card-${h.symbol}`}>
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="min-w-0">
          <div className="font-medium flex items-center gap-1.5 flex-wrap">
            <span>{h.symbol}</span>
            <span
              className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{
                background: h.exchange === "BSE" ? "var(--tint-neutral-bg)" : "var(--tint-accent-bg)",
                color: h.exchange === "BSE" ? "var(--tint-neutral-fg)" : "var(--tint-accent-fg)",
              }}
            >
              {h.exchange || "NSE"}
            </span>
            {h.market_cap && (
              <span className="text-[9px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--tint-accent-bg)", color: "var(--tint-accent-fg)" }}>
                {MCAP_LABEL[h.market_cap]}
              </span>
            )}
          </div>
          {h.company_name && <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--text-muted)" }}>{h.company_name}</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-sm font-medium">{fmtINR(h.current_value)}</div>
          <div className={`font-mono text-[11px] ${colorByValue(h.unrealized_gain)}`}>
            {fmtINR(h.unrealized_gain, { showSign: true })} · {fmtPct(h.unrealized_pct)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        {showQty && (
          <CardCell label="Qty" value={fmtQty(h.quantity)} />
        )}
        {showAvg && (
          <CardCell label="Avg" value={fmtINR(h.avg_cost, { decimals: 2 })} />
        )}
        <CardCell label="LTP" value={h.current_price > 0 ? fmtINR(h.current_price) : "—"} />
        {h.previous_close && (
          <CardCell label="Day" value={fmtINR(h.day_gain, { showSign: true })} sub={fmtPct(h.day_gain_pct)} tone={h.day_gain} />
        )}
      </div>
    </div>
  );
}

function CardCell({ label, value, sub, tone }) {
  const toneClass = tone === undefined ? "" : colorByValue(tone);
  return (
    <div>
      <div className="label-overline text-[9px]" style={{ letterSpacing: "0.15em" }}>{label}</div>
      <div className={`font-mono mt-0.5 ${toneClass}`}>{value}</div>
      {sub && <div className="text-[9px] font-mono" style={{ color: "var(--text-muted)" }}>{sub}</div>}
    </div>
  );
}
